import { useState, useCallback } from "react";
import RatingStars from "../shared/RatingStars";
import RatingInput from "../shared/RatingInput";
import { API_BASE, authHeaders, handleResponse } from "../../api/api";
import { useAuth } from "../../contexts/AuthContext";
import "./Reviews.css";

const fmt = (dt) =>
  new Date(dt).toLocaleDateString("en-KE", { year: "numeric", month: "short", day: "numeric" });

const RatingBar = ({ star, count, total }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="rv-bar-row" aria-label={`${star} star: ${count} review${count !== 1 ? "s" : ""}`}>
      <span className="rv-bar-label">{star}</span>
      <div className="rv-bar-track" role="presentation">
        <div className="rv-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="rv-bar-count">{count}</span>
    </div>
  );
};

const ReviewCard = ({ review }) => (
  <article className="rv-card" aria-label={`Review by ${review.author_name ?? "Customer"}`}>
    <header className="rv-card-header">
      <span className="rv-avatar" aria-hidden="true">
        {(review.author_name ?? "?")[0].toUpperCase()}
      </span>
      <div className="rv-card-meta">
        <span className="rv-author">{review.author_name ?? "Customer"}</span>
        {review.verified_purchase && (
          <span className="rv-verified" aria-label="Verified purchase">✓ Verified Purchase</span>
        )}
      </div>
      <div className="rv-card-right">
        <RatingStars rating={review.rating} size="sm" />
        <time className="rv-date" dateTime={review.created_at}>{fmt(review.created_at)}</time>
      </div>
    </header>
    {review.body && <p className="rv-body">{review.body}</p>}
  </article>
);

const Reviews = ({ productId, reviews = [], averageRating = null, reviewCount = null, onReviewSubmitted }) => {
  const { user, token } = useAuth();

  const [rating, setRating]     = useState(0);
  const [body, setBody]         = useState("");
  const [submitting, setSub]    = useState(false);
  const [submitErr, setErr]     = useState(null);
  const [submitted, setDone]    = useState(false);

  // Prefer the real backend aggregate (over ALL reviews) when the caller
  // provides one — the loaded `reviews` list is capped (see
  // ProductDetailPage.jsx's limit=50 fetch), so a client-side average would
  // silently drift from reality for any product with more reviews than that.
  const total = reviewCount !== null ? reviewCount : reviews.length;
  const avg   = averageRating !== null ? averageRating : (reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0);

  const dist = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: reviews.filter((r) => r.rating === star).length,
  }));

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!rating) { setErr("Please select a star rating."); return; }
      setSub(true);
      setErr(null);
      try {
        const res = await fetch(`${API_BASE}/products/${productId}/reviews`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders(token) },
          body: JSON.stringify({ rating, body }),
        });
        await handleResponse(res);
        setDone(true);
        onReviewSubmitted?.();
      } catch (err) {
        setErr(
          err.status === 403
            ? "Only customers who have received this product can leave a review."
            : err.status === 409
            ? "You have already reviewed this product."
            : err.message || "Failed to submit review. Please try again."
        );
      } finally {
        setSub(false);
      }
    },
    [rating, body, productId, token, onReviewSubmitted]
  );

  return (
    <section className="rv-section" aria-labelledby="rv-heading">
      <h2 className="rv-heading" id="rv-heading">Customer Reviews</h2>

      {/* Summary */}
      {total > 0 ? (
        <div className="rv-summary">
          <div className="rv-avg-block">
            <span className="rv-avg-num">{avg.toFixed(1)}</span>
            <RatingStars rating={avg} size="md" />
            <span className="rv-avg-label">{total} review{total !== 1 ? "s" : ""}</span>
          </div>
          <div className="rv-bars">
            {dist.map(({ star, count }) => (
              <RatingBar key={star} star={star} count={count} total={total} />
            ))}
          </div>
        </div>
      ) : (
        <p className="rv-empty">No reviews yet — be the first!</p>
      )}

      {/* Submit form */}
      <div className="rv-form-wrap">
        <h3 className="rv-form-heading">Write a Review</h3>
        {!user ? (
          <p className="rv-gate-msg">
            <a href="/login" className="rv-gate-link">Sign in</a> to leave a review.
          </p>
        ) : submitted ? (
          <p className="rv-success" role="alert">
            ✓ Thank you! Your review has been submitted.
          </p>
        ) : (
          <form className="rv-form" onSubmit={handleSubmit} noValidate>
            <div className="rv-field">
              <label className="rv-label" id="rv-rating-label">Your Rating</label>
              <RatingInput
                value={rating}
                onChange={setRating}
                disabled={submitting}
                aria-labelledby="rv-rating-label"
              />
            </div>
            <div className="rv-field">
              <label className="rv-label" htmlFor="rv-body">Your Review <span className="rv-optional">(optional)</span></label>
              <textarea
                id="rv-body"
                className="rv-textarea"
                rows={4}
                maxLength={1200}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                disabled={submitting}
                placeholder="Tell others about your experience with this product..."
              />
              <span className="rv-char-count">{body.length}/1200</span>
            </div>
            {submitErr && <p className="rv-error" role="alert">{submitErr}</p>}
            <button
              type="submit"
              className="rv-submit-btn"
              disabled={submitting || !rating}
            >
              {submitting ? "Submitting…" : "Submit Review"}
            </button>
          </form>
        )}
      </div>

      {/* Review list */}
      {total > 0 && (
        <div className="rv-list" aria-label="Customer reviews">
          {reviews.map((r) => (
            <ReviewCard key={r.id} review={r} />
          ))}
        </div>
      )}
    </section>
  );
};

export default Reviews;
